/**
 * Tech→MD artifact QC workflow helpers (Epic B3).
 * Elevates impedance heatmap / QC sign-off into physician-queue gates.
 */

import {
  ARTIFACT_QC_EXCLUDED_USE,
  ARTIFACT_QC_INTENDED_USE,
  buildImpedanceTimeline,
  createPendingQcSignOff,
  rejectQcSignOff,
  signOffQc,
  type ElectrodeQualitySample,
  type ImpedanceTimelinePoint,
  type QcSignOffRecord,
} from "./artifactQcTypes";

export const TECH_QC_WORKFLOW_SCHEMA_VERSION = "tech-qc-workflow-v1.0.0";

export type ElectrodeHeatmapCell = {
  channel: string;
  impedanceKohms: number | null;
  quality: ElectrodeQualitySample["quality"];
  isBad: boolean;
  heat: number;
};

export type TechQcWorkbenchModel = {
  schemaVersion: string;
  intendedUse: string;
  excludedUse: string;
  heatmap: ElectrodeHeatmapCell[];
  timeline: ImpedanceTimelinePoint[];
  signOff: QcSignOffRecord;
  physicianQueueBlocked: boolean;
  badChannelCount: number;
};

export function buildElectrodeHeatmap(
  samples: ElectrodeQualitySample[],
): ElectrodeHeatmapCell[] {
  return samples.map((sample) => {
    const z = sample.impedanceKohms;
    let heat = 0.2;
    if (typeof z === "number" && Number.isFinite(z)) {
      heat = Math.max(0, Math.min(1, z / 20));
    } else if (sample.isBad || sample.quality === "poor") {
      heat = 0.9;
    } else if (sample.quality === "fair") {
      heat = 0.5;
    }
    return {
      channel: sample.channel,
      impedanceKohms: sample.impedanceKohms,
      quality: sample.quality,
      isBad: sample.isBad,
      heat,
    };
  });
}

export function buildTechQcWorkbench(input: {
  electrodes: ElectrodeQualitySample[];
  impedanceSamples?: Array<{
    timeSeconds: number;
    channel: string;
    impedanceKohms: number;
  }>;
  signOff?: QcSignOffRecord;
}): TechQcWorkbenchModel {
  const signOff = input.signOff || createPendingQcSignOff();
  const heatmap = buildElectrodeHeatmap(input.electrodes);
  return {
    schemaVersion: TECH_QC_WORKFLOW_SCHEMA_VERSION,
    intendedUse: ARTIFACT_QC_INTENDED_USE,
    excludedUse: ARTIFACT_QC_EXCLUDED_USE,
    heatmap,
    timeline: buildImpedanceTimeline(input.impedanceSamples || []),
    signOff,
    physicianQueueBlocked: signOff.blocksPhysicianReporting,
    badChannelCount: heatmap.filter((cell) => cell.isBad || cell.quality === "poor")
      .length,
  };
}

export function completeTechQcPass(input: {
  technologistId: string;
  notes?: string | null;
}): QcSignOffRecord {
  return signOffQc(input);
}

export function completeTechQcFail(input: {
  technologistId: string;
  notes?: string | null;
}): QcSignOffRecord {
  return rejectQcSignOff(input);
}

export function canPromoteToPhysicianQueue(signOff: QcSignOffRecord): boolean {
  return signOff.status === "signed" && !signOff.blocksPhysicianReporting;
}

export function canFinalizeReportWithQc(input: {
  signOff: QcSignOffRecord | null | undefined;
  requireQc?: boolean;
}): boolean {
  if (input.requireQc === false) return true;
  if (!input.signOff) return false;
  return canPromoteToPhysicianQueue(input.signOff);
}
